import { ADD_APPOINTMENT_TABS } from 'AppointmentBox/helper';
import { modalConfirmSMSChangeRef } from 'AppointmentBox/ModalConfirmSMSChange';
import aptSignal from 'AppointmentBox/signal';
import { tableLoading } from 'AppointmentList/Table';
import { blockHourTableLoading } from 'AppointmentList/Table/BlockHourTable';
import { AxiosResponse } from 'axios';
import { calendarScrollTo } from 'Calendar';
import { ANYONE_ID } from 'Calendar/anyone';
import calcTo from 'Calendar/helper';
import { ICalendarItemData } from 'Calendar/types';
import { customerTableLoading } from 'CalendarFilter/CustomerSearch/AppointmentByCustomer';
import { calendarDatePickerRef, dateParamSignal } from 'CalendarFilter/DatePicker';
import { loadingSignal } from 'components/Loading';
import { STAFF_CALENDAR_PAGE_SIZE, STAFF_CALENDAR_PAGE_ZOOM_SIZE } from 'constants/calendar';
import first from 'lodash/first';
import get from 'lodash/get';
import moment from 'utils/moment';
import qbSignal from 'QuickBooking/signal';
import { all, call, delay, put, select, takeLatest } from 'redux-saga/effects';
import store, { RootState } from 'store';
import { ISagaFunc } from 'store/actionConfigs';
import { IShopAllSetting } from 'services/types/shop';
import { ICategoryUI } from 'types/category';
import { ICustomerUI } from 'types/customer';
import { IServiceUI } from 'types/service';
import { IStaffUI } from 'types/staff';
import storage from 'utils/sessionStorage';
import toast from 'utils/toast';
import { configureMomentTimezone } from 'utils/moment';
import CallerContext from 'widgets/CallerId/context';
import CallerV2 from 'Calendar/CallerV2';
import { isCallerV2Enabled } from 'Calendar/CallerV2/feature-flag';
import aptActions from './actions/apt';
import commActions from './actions/comm';
import shopActions from './actions/shop';
import apis from './apis';
import { mappingResToCalendarDataItem } from './helpers';
import { aptUIActions } from './reducers/apt';
import { commUIActions } from './reducers/comm';
import { customerSelectors, tableSelectors } from './selectors/apt';
import commSelectors from './selectors/comm';
import { IApiStaffByAppointmentParams, IApiStaffByAppointmentResData, IAppointmentSummaryCallerIdParams, ICalendarDropData } from './types/apt';
import { IBlockHourItemData, IBlockHourResItem, IBodyAddBlockHours, IBlockHourHistoryItem, IBlockHourHistoriesQuery, IBlockHourHistoriesResponse } from './types/blockHours';
import { IApiHistoryCallerIdParams, ICallerIdHistoryResData, IPendingCallerResItem, ICustomerRecentApptsResData, ICustomerApptsPageParams, ICustomerApptsPageResData } from './types/caller';
import { IApiGetQuickCommissionsParams } from './types/comm';
import { IBodyApiQuickBooking, IQuickBookingServiceItem } from './types/quickBooking';
import { IAppointmentListParams, IPayloadBooking, IPayloadBookingV2, IPayloadDragADrop } from './types/request';
import { IAppointmentResItem, IAptListResPayload, IBookingAptResData, ICategoryResItem, ICustomerResItem, ISchedulerSalonResData, IServiceResItem, IStaffResItem } from './types/response';

const mappingAppointment = (appointmentRes: IAppointmentResItem[]) => appointmentRes.map(mappingResToCalendarDataItem);

const getCalendarData: ISagaFunc<{ shopId?: string; startTime: string }> = function* ({ payload }) {
  yield put(aptActions.getStaffByAppointment.fetch({
    date: payload.startTime || '',
  }));
};

const mappingBlockHour = (blockHourItems: IBlockHourResItem[]) => {
  const data: ICalendarItemData[] = blockHourItems.map(o => {
    const id = o.id?.toString();
    const staffId = o.staffId?.toString();
    const startTime = moment(o.startTime, 'MM/DD/YYYY HH:mm A').format('MM-DD-YYYY HH:mm:ss');
    const endTime = moment(o.endTime, 'MM/DD/YYYY HH:mm A').format('MM-DD-YYYY HH:mm:ss');
    return ({
      id,
      type: 'block-hour',
      data: {
        id,
        staffId, startTime, endTime,
        note: o.reason || '',
      } as IBlockHourItemData,
      staffId, startTime, endTime,
    });
  });
  return data;
};

const getCalendarBlockHourList: ISagaFunc<{ shopId?: string, startTime: string }> = function* ({ payload }) {
  try {
    const res: AxiosResponse<{ payload: IBlockHourResItem[] }> = yield call(apis.getAllBlockHoursAptList, payload.shopId || storage.shopId.get(), {
      startTime: payload.startTime,
      endTime: payload.startTime,
    });
    const blockHourItems = res?.data?.payload;
    if (blockHourItems) {
      yield put(aptActions.calendar.setBlockHoursData(mappingBlockHour(blockHourItems)));
    }
  } catch (error) {

  } finally {
  }
};

const getCategories: ISagaFunc<string> = function* ({ payload: shopId }) {
  try {
    const { cate, ser } = yield all({ cate: call(apis.getCategories, shopId), ser: call(apis.getServices, shopId) });

    const categoriesRes: ICategoryResItem[] = cate?.data?.payload;
    const servicesRes: IServiceResItem[] = ser?.data?.payload;
    if (categoriesRes && servicesRes) {
      const data: ICategoryUI[] = categoriesRes.map((o) => ({
        id: o.id?.toString(),
        name: o.categoryName,
        services: servicesRes.filter(s => s.catId === o.id).map(s => ({
          cateId: o.id?.toString(),
          id: s.id?.toString(),
          image: s.urlImage,
          name: s.serviceName,
          duration: s.duration,
          price: s.price,
        }) as IServiceUI),
      }));
      yield put(shopActions.category.success(data));
    }
  } catch (error) {

  }
};

const getStaffs = function* (shopId: string) {
  try {
    const res: AxiosResponse<{ payload: IStaffResItem[] }> = yield call(apis.getStaffsAvailable, shopId, dateParamSignal.value?.format('YYYY-MM-DD') || '');
    const staffRes = res?.data?.payload;
    if (staffRes) {
      const data: IStaffUI[] = staffRes.map((o, i) => ({
        avatar: o.urlImage,
        extraData: o,
        id: o.id?.toString(),
        name: o.nickName || o.firstName,
        page: getPageNumberByIndex(i),
        pageZoom: getPageNumberByIndex(i, STAFF_CALENDAR_PAGE_ZOOM_SIZE),
        timeSchedule: o.timeSchedule,
      }));
      yield put(shopActions.staff.success(data));
    }
  } catch (error) {

  }
};

const getPureStaffs: ISagaFunc<string> = function* ({ payload: shopId }) {
  try {
    const res: AxiosResponse<{ payload: IStaffResItem[] }> = yield call(apis.getStaffs, shopId);
    const staffRes = res?.data?.payload;
    if (staffRes) {
      const data: IStaffUI[] = staffRes.map((o, i) => ({
        avatar: o.urlImage,
        extraData: o,
        id: o.id?.toString(),
        name: o.nickName || o.firstName,
        page: getPageNumberByIndex(i),
        pageZoom: getPageNumberByIndex(i, STAFF_CALENDAR_PAGE_ZOOM_SIZE),
        timeSchedule: o.timeSchedule,
      }));
      yield put(shopActions.pureStaffs.success(data));
      yield put(shopActions.staff.success(data));
    }
  } catch (error) {

  }
};

function getPageNumberByIndex(index: number, pageSize = STAFF_CALENDAR_PAGE_SIZE): number {
  if (index < 0) return 0; // Optional: you might want to handle negative indices differently
  return Math.floor(index / pageSize) + 1;
}

const getStaffsAvailable: ISagaFunc<string> = function* ({ payload }) {
  try {
    const res: AxiosResponse<{ payload: IStaffResItem[] }> = yield call(apis.getStaffsAvailable, storage.shopId.get(), payload || dateParamSignal.value?.format('YYYY-MM-DD') || '');
    const staffRes = res?.data?.payload;
    if (staffRes) {
      const data: IStaffUI[] = staffRes.map((o, i) => ({
        avatar: o.urlImage,
        extraData: o,
        id: o.id?.toString(),
        name: o.nickName || o.firstName,
        page: getPageNumberByIndex(i),
        pageZoom: getPageNumberByIndex(i, STAFF_CALENDAR_PAGE_ZOOM_SIZE),
        timeSchedule: o.timeSchedule,
      }));
      yield put(shopActions.staffAvailable.success(data));
      yield put(shopActions.staff.success(data));
    }
  } catch (error) {
  }
};

const getBlockHourHistories: ISagaFunc<IBlockHourHistoriesQuery> = function* ({ payload }) {
  try {
    const res = yield call(apis.getBlockHourHistories, storage.shopId.get(), payload);
    if(res?.data?.payload) {
      yield put(shopActions.blockHourHistories.success(res.data.payload));
    }
  } catch (error) {
    yield put(shopActions.blockHourHistories.fail(error));
  }
};

const getScheduleTimeOffSlots: ISagaFunc<{ date?: string; staffId?: string }> = function* ({ payload }) {
  try {
    const currentDate = payload?.date || dateParamSignal.value?.format('YYYY-MM-DD') || moment().format('YYYY-MM-DD');
    const staffId = payload?.staffId || '';
    const res: AxiosResponse<{ payload: { timeSlots: string[] } }> = yield call(apis.getTimeSlot, storage.shopId.get(), currentDate, staffId);
    const timeSlots = res?.data?.payload?.timeSlots || [];
    yield put(shopActions.scheduleTimeOffSlots.success(timeSlots));
  } catch (error) {
    yield put(shopActions.scheduleTimeOffSlots.fail(error));
  }
};

const getStaffActions = function* () {
  yield getStaffs(storage.shopId.get());
};

const getCustomers: ISagaFunc<string> = function* ({ payload: shopId }) {
  try {
    const res: AxiosResponse<{ payload: { customers: ICustomerResItem[] } }> = yield call(apis.getAllCustomerAutoSearchPage, shopId, '');

    const customerRes = res?.data?.payload?.customers;
    if (customerRes) {
      const data: ICustomerUI[] = customerRes.map(o => ({
        id: o.id?.toString(),
        name: o.name,
        phone: o.phone,
        point: o.point,
      }));
      yield put(shopActions.customer.success(data));
    }

  } catch (error) {

  }
};

const getSalonInfo: ISagaFunc<string> = function* ({ payload: shopId }) {
  try {
    const res: AxiosResponse<{ payload: any }> = yield call(apis.getSalonInfo, shopId);
    if (res.data.payload) {
      configureMomentTimezone(res.data.payload.timeZone);
      yield put(shopActions.salonInfo.success(res.data.payload));
    }
  } catch (error) { }
};

const getShopAllSetting: ISagaFunc<string> = function* ({ payload: shopId }) {
  try {
    const res: AxiosResponse<{ payload: IShopAllSetting }> = yield call(apis.getShopAllSetting, shopId);
    if (res.data.payload) {
      // res.data.payload.enableCallerV2 = true;
      yield put(shopActions.shopAllSetting.success(res.data.payload));
    }
  } catch (error) { }
};

const initData: ISagaFunc<string> = function* ({ payload }) {
  yield storage.shopId.set(payload);
  yield delay(200);
  yield all([
    put(aptActions.getStaffByAppointment.fetch({ date: dateParamSignal.value?.format('YYYY-MM-DD') || '', page: 1, size: 10, shopId: payload })),
    put(aptActions.blockHourList.fetch()),
    put(shopActions.category.fetch(payload)),
    put(shopActions.pureStaffs.fetch(payload)),
    put(shopActions.salonInfo.fetch(payload)),
    put(shopActions.shopAllSetting.fetch(payload)),
    put(shopActions.customer.fetch(payload)),
    put(aptActions.quickBooking.getServices.fetch(payload)),
  ]);
  // @ts-ignore
  window?.ReactNativeWebView?.postMessage('WEB_READY');
  yield getSalonScheduler();
};

const getAptList = function* () {
  const res: AxiosResponse<{ payload: { data: IAppointmentResItem[] } }> = yield call(apis.getAppointments, storage.shopId.get(), dateParamSignal.value?.format('YYYY-MM-DD') || '');
  const appointmentRes = res?.data?.payload?.data;
  if (appointmentRes) {
    const data: ICalendarItemData[] = mappingAppointment(appointmentRes);
    yield put(aptActions.calendar.getData.success(data));
  }
};
const getBlockHourList = function* () {
  const res: AxiosResponse<{ payload: IBlockHourResItem[] }> = yield call(apis.getAllBlockHoursAptList, storage.shopId.get(), {
    startTime: dateParamSignal.value?.format('YYYY-MM-DD') || '',
    endTime: dateParamSignal.value?.format('YYYY-MM-DD') || '',
  });
  const blockHourItems = res?.data?.payload;
  if (blockHourItems) {
    const data: ICalendarItemData[] = mappingBlockHour(blockHourItems);
    yield put(aptActions.calendar.setBlockHoursData(data));
  }
};

const bookApt: ISagaFunc<IPayloadBooking> = function* ({ payload }) {
  loadingSignal.value = true;
  try {
    const apt: AxiosResponse<{ payload: IBookingAptResData }> = yield call(apis.bookAppointment, storage.shopId.get(), payload);
    if (apt.data.payload) {
      const date = moment(payload.payload.startTime, 'MM/DD/YYYY HH:mm');
      dateParamSignal.value = date;
      calendarDatePickerRef.current?.setSelectedDate(date);
      aptSignal.close();
      setTimeout(() => {
        const id = dateParamSignal?.value?.format('HH:mm') + '/' + (payload.payload.staffId || ANYONE_ID);
        const element = document.getElementById(id);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }, 1000);
      getAptList();
    }
  } catch (error: any) {
    const errorCode = get(error, 'response.data.code');
    if (errorCode === 1170) {
      aptSignal.tabSignal.value = ADD_APPOINTMENT_TABS.TIME;
    }
  }
  finally {
    loadingSignal.value = false;
  }
};

const bookAptV2: ISagaFunc<IPayloadBookingV2> = function* ({ payload }) {
  loadingSignal.value = true;
  try {
    const apt: AxiosResponse<{ payload: IAppointmentResItem[] }> = yield call(apis.bookAppointmentV2, storage.shopId.get(), payload);
    const firstItem = first(payload.items);
    if (apt.data.payload) {
      yield put(aptUIActions.upsertAppointments(apt.data.payload));
      const date = moment(firstItem?.startTime, 'MM/DD/YYYY HH:mm');
      dateParamSignal.value = date;
      calendarDatePickerRef.current?.setSelectedDate(date);
      aptSignal.close();
      toast.success('Booking Success');
      const staffId = firstItem?.staffId || ANYONE_ID;
      const staffsAvailable: IStaffUI[] = yield select((state: RootState) => state.shop.staffsAvailable) || [];
      const currentStaffPage = staffsAvailable.find(o => o.id?.toString() === staffId?.toString())?.page;
      if (currentStaffPage) {
        yield put(aptUIActions.setCurrentPage(currentStaffPage));
      }
      setTimeout(() => {
        const id = dateParamSignal?.value?.format('HH:mm') + '/' + (firstItem?.staffId || ANYONE_ID);
        const element = document.getElementById(id);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }, 1000);
    }
  } catch (error: any) {
    const errorCode = get(error, 'response.data.code');
    if (errorCode === 1170) {
      aptSignal.tabSignal.value = ADD_APPOINTMENT_TABS.TIME;
    }
  }
  finally {
    loadingSignal.value = false;
  }
};
const addBlockHour: ISagaFunc<IBodyAddBlockHours> = function* ({ payload }) {
  loadingSignal.value = true;

  try {
    const apt: AxiosResponse<{ payload: any }> = yield call(apis.addBlockHours, payload);
    if (apt.data.payload) {
      const date = moment(payload?.startTime, 'MM/DD/YYYY HH:mm');
      dateParamSignal.value = date;
      calendarDatePickerRef.current?.setSelectedDate(date);
      setTimeout(() => {
        const id = dateParamSignal?.value?.format('HH:mm') + '/' + (payload.staffId || ANYONE_ID);
        const element = document.getElementById(id);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }, 1000);
      yield getBlockHourList();
    }
  } catch (error) {
    toast.error('An error occurred, please try again!');
  }
  finally {
    loadingSignal.value = false;
  }
};

const deleteApt: ISagaFunc<{ aptId: string, pinCode: string }> = function* ({ payload }) {
  loadingSignal.value = true;
  try {
    const apt: AxiosResponse<{ payload: string }> = yield call(apis.deleteApt, storage.shopId.get(), payload.aptId, payload.pinCode);
    if (apt.data.payload) {
      yield getAptList();
      aptSignal.close();
    }
  } catch (error) {
    toast.error('An error occurred, please try again!');
  }
  finally {
    loadingSignal.value = false;
  }
};

const cancelApt: ISagaFunc<{ aptId: string, reason: string, pinCode: string }> = function* ({ payload }) {
  loadingSignal.value = true;
  try {
    const apt: AxiosResponse<{ payload: IAppointmentResItem[] }> = yield call(apis.cancelApt, storage.shopId.get(), payload);
    if (apt.data.payload) {
      aptSignal.close();
      yield put(aptUIActions.upsertAppointments(apt.data.payload));
    }
  } catch (error) { }
  finally {
    loadingSignal.value = false;
  }
};
const confirmApt: ISagaFunc<{ appointmentId: string, pinCode: string }> = function* ({ payload }) {
  loadingSignal.value = true;
  try {
    const apt: AxiosResponse<{ payload: IAppointmentResItem[] }> = yield call(apis.confirmApt, storage.shopId.get(), payload);
    if (apt.data.payload) {
      aptSignal.close();
      yield put(aptUIActions.upsertAppointments(apt.data.payload));
    }
  } catch (error) { }
  finally {
    loadingSignal.value = false;
  }
};

const checkInApt: ISagaFunc<string> = function* ({ payload }) {
  loadingSignal.value = true;
  try {
    const apt: AxiosResponse<{ payload: IAppointmentResItem[] }> = yield call(apis.checkInApt, storage.shopId.get(), payload);
    if (apt.data.payload) {
      yield put(aptUIActions.upsertAppointments(apt.data.payload));
      aptSignal.close();
    }
  } catch (error) { }
  finally {
    loadingSignal.value = false;
  }
};

const deleteBlockHour: ISagaFunc<string> = function* ({ payload }) {
  loadingSignal.value = true;
  try {
    const res = yield call(apis.removeBlockHour, payload);
    if (res.data.payload) {
      yield getBlockHourList();
    }
  } catch (error) { }
  finally {
    loadingSignal.value = false;
  }
};
const onFormDeleteBlockHourSuccess: ISagaFunc<string> = function* ({ payload }) {
  if (dateParamSignal.value?.format('YYYY-MM-DD') !== payload) return;
  try {
    yield getBlockHourList();
  } catch (error) { }
};

const updateApt: ISagaFunc<IPayloadBooking> = function* ({ payload }) {
  loadingSignal.value = true;
  try {
    const apt: AxiosResponse<{ payload: string }> = yield call(apis.updateApt, storage.shopId.get(), payload);
    if (apt.data.payload) {
      const date = moment(payload.payload.startTime, 'MM/DD/YYYY HH:mm');
      dateParamSignal.value = date;
      calendarDatePickerRef.current?.setSelectedDate(date);
      aptSignal.close();
      setTimeout(() => {
        const id = dateParamSignal?.value?.format('HH:mm') + '/' + (payload.payload.staffId || ANYONE_ID);
        const element = document.getElementById(id);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }, 1000);
      yield getAptList();
    }
  } catch (error) { }
  finally {
    loadingSignal.value = false;
  }
};

const getList = function* (date: string) {
  const staffSize: number = yield select((state: RootState) => state.apt.staffSize);
  yield put(aptActions.getStaffByAppointment.fetch({ date, page: 1, size: staffSize, shopId: storage.shopId.get() }));
};

const updateAptV2: ISagaFunc<IPayloadBookingV2> = function* ({ payload }) {
  loadingSignal.value = true;
  try {
    const apt: AxiosResponse<{ payload: IAppointmentResItem[] }> = yield call(apis.updateAptV2, storage.shopId.get(), payload);
    const firstItem = first(payload.items);
    if (apt.data.payload) {
      yield put(aptUIActions.upsertAppointments(apt.data.payload));
      modalConfirmSMSChangeRef.current?.open(payload.id || '');
      const date = moment(firstItem?.startTime, 'MM/DD/YYYY HH:mm');
      dateParamSignal.value = date;
      calendarDatePickerRef.current?.setSelectedDate(date);
      yield getList(date.format('YYYY-MM-DD'));
      aptSignal.close();
      toast.success('Update Success');
      const staffId = firstItem?.staffId || ANYONE_ID;
      const staffsAvailable: IStaffUI[] = yield select((state: RootState) => state.shop.staffsAvailable) || [];
      const currentStaffPage = staffsAvailable.find(o => o.id?.toString() === staffId?.toString())?.page;
      if (currentStaffPage) {
        yield put(aptUIActions.setCurrentPage(currentStaffPage));
      }
      setTimeout(() => {
        const id = dateParamSignal?.value?.format('HH:mm') + '/' + (firstItem?.staffId || ANYONE_ID);
        const element = document.getElementById(id);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }, 1000);
    }
  } catch (error) { }
  finally {
    loadingSignal.value = false;
  }
};

const updateBlockHour: ISagaFunc<IBodyAddBlockHours> = function* ({ payload }) {
  loadingSignal.value = true;
  try {
    const apt: AxiosResponse<{ payload: string }> = yield call(apis.updateBlockHour, storage.shopId.get(), payload);
    if (apt.data.payload) {
      const date = moment(payload.startTime, 'MM/DD/YYYY HH:mm');
      dateParamSignal.value = date;
      calendarDatePickerRef.current?.setSelectedDate(date);
      setTimeout(() => {
        const id = dateParamSignal?.value?.format('HH:mm') + '/' + (payload.staffId || ANYONE_ID);
        const element = document.getElementById(id);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }, 1000);
      yield getBlockHourList();
    }
  } catch (error) {
    toast.error('An error occurred, please try again!');
  }
  finally {
    loadingSignal.value = false;
  }
};

const calendarUpdateDrop: ISagaFunc<ICalendarDropData> = function* ({ payload }) {
  loadingSignal.value = true;
  const startTime = moment(payload.startTime, 'MM-DD-YYYY HH:mm:ss');
  const body: IPayloadDragADrop = {
    payload: {
      appointmentId: +payload.id,
      startTime: startTime.format('MM-DD-YYYY HH:mm:ss'),
      endTime: startTime.clone().add(payload.distance, 'minutes').format('MM-DD-YYYY HH:mm:ss'),
      staffId: +(payload.staffId || ''),
      pinCode: payload.pinCode,
    }
  };
  try {
    const res: AxiosResponse<{ payload: IAppointmentResItem[] }> = yield call(apis.updateDragAndDrop, storage.shopId.get(), body);
    if (res.data.payload) {
      modalConfirmSMSChangeRef.current?.open(payload.id);
      yield put(aptUIActions.upsertAppointments(res.data.payload));
    }

  } catch (error) {
    toast.error('An error occurred, please try again!');
  }
  finally {
    loadingSignal.value = false;
  }
};

const getAppointmentLists: ISagaFunc<IAppointmentListParams> = function* ({ payload }) {
  tableLoading.value = true;
  try {
    const res: AxiosResponse<{ payload: IAptListResPayload }> = yield call(apis.getAppointmentLists, storage.shopId.get(), payload);
    if (res?.data?.payload) {
      yield put(aptActions.table.getData.success(res.data.payload));
    }
  } catch (error) { }
  finally {
    tableLoading.value = false;
  }
};

const getCustomerAppointmentLists: ISagaFunc<IAppointmentListParams> = function* ({ payload }) {
  customerTableLoading.value = true;
  try {
    const res: AxiosResponse<{ payload: IAptListResPayload }> = yield call(
      apis.getAppointmentLists,
      storage.shopId.get(),
      payload
    );
    if (res?.data?.payload) {
      yield put(aptActions.customer.getData.success(res.data.payload));
    }
  } catch (error) {
  } finally {
    customerTableLoading.value = false;
  }
};

const getAptListBlockHourList: ISagaFunc<IAppointmentListParams> = function* ({ payload }) {
  blockHourTableLoading.value = true;
  try {
    const res: AxiosResponse<{ payload: IBlockHourResItem[] }> = yield call(apis.getAllBlockHoursAptList, storage.shopId.get(), {
      endTime: payload.end_time || '',
      staffId: payload.staffId || '',
      startTime: payload.start_time || '',
    });
    const blockHourItems = res?.data?.payload;
    if (blockHourItems) {
      yield put(aptActions.table.blockHours.success(blockHourItems));
    }
  } catch (error) {
  } finally {
    blockHourTableLoading.value = false;
  }
};
const setTableData: ISagaFunc<IAppointmentListParams> = function* ({ payload }) {
  const _oldParams = yield select(tableSelectors.getPureParams);
  const params: IAppointmentListParams = {
    ..._oldParams || {},
    ...payload || {},
  };
  if (params.staffId === undefined) {
    delete params.staffId;
  }
  if (params.type === -1) {
    delete params.type;
  }
  yield put(aptActions.table.getData.fetch(params));
};

const setCustomerTableData: ISagaFunc<IAppointmentListParams> = function* ({ payload }) {
  const _oldParams = yield select(customerSelectors.getPureParams);
  const params: IAppointmentListParams = {
    ...(_oldParams || {}),
    ...(payload || {}),
  };
  if (params.staffId === undefined) {
    delete params.staffId;
  }
  if (params.type === -1) {
    delete params.type;
  }
  yield put(aptActions.customer.getData.fetch(params));
};

const prolongedTime: ISagaFunc<{ id: string, status: 'ADD' | 'MINUS', pinCode: string }> = function* ({ payload }) {
  loadingSignal.value = true;
  try {
    const res: AxiosResponse<{ payload: IAppointmentResItem[] }> = yield call(apis.prolongedTime, payload.id, payload.status, payload.pinCode);
    if (res.data.payload) {
      yield put(aptUIActions.upsertAppointments(res.data.payload));
    }
  } catch (error) { }
  finally {
    loadingSignal.value = false;
  }
};

const getStaffAvailableBooking: ISagaFunc<{ startTime: string, endTime: string }> = function* ({ payload }) {
  try {
    const res: AxiosResponse<{ payload: IStaffResItem[] }> = yield call(apis.getStaffAvailableBooking, payload);
    if (res.data.payload) {
      const data: IStaffUI[] = res.data.payload.map((o, i) => ({
        avatar: o.urlImage,
        extraData: o,
        id: o.id?.toString(),
        name: o.nickName || o.firstName,
        page: getPageNumberByIndex(i),
        pageZoom: getPageNumberByIndex(i, STAFF_CALENDAR_PAGE_ZOOM_SIZE),
        timeSchedule: o.timeSchedule,
      }));
      yield put(aptActions.staffAvailableBooking.success(data));
    }
  } catch (error) {
    yield put(aptActions.staffAvailableBooking.fail({}));
  }
};

const prolongedTimeBlockHour: ISagaFunc<{ id: string, status: 'ADD' | 'MINUS' }> = function* ({ payload }) {
  loadingSignal.value = true;
  try {
    yield call(apis.prolongedTimeBlockHour, payload.id, payload.status);
  } catch (error) { }
  finally {
    yield getBlockHourList();
    loadingSignal.value = false;
  }
};

const getSalonScheduler = function* () {
  try {
    const res: AxiosResponse<{ payload: ISchedulerSalonResData }> = yield call(apis.getSalonScheduler);
    if (res.data.payload) {
      yield put(shopActions.scheduler.success(res.data.payload));
    }
  } catch (error) {
    yield put(shopActions.scheduler.fail({}));
  }
};

const refreshCalendar = function* () {
  loadingSignal.value = true;
  yield getAptList();
  const pixel = calcTo(moment().format('MM-DD-YYYY HH:mm:ss'));
  calendarScrollTo.value(pixel - 10);
  loadingSignal.value = false;
};

const quickBookingGetServices: ISagaFunc<string> = function* ({ payload }) {
  try {
    const res: AxiosResponse<{ payload: IQuickBookingServiceItem[] }> = yield call(apis.getQuickBookingServices, payload);
    if (res.data.payload) {
      yield put(aptActions.quickBooking.getServices.success(res.data.payload));
    }
  } catch (error) { }
};

const doQuickBooking: ISagaFunc<IBodyApiQuickBooking[]> = function* ({ payload }) {
  loadingSignal.value = true;
  try {
    const apt: AxiosResponse<{ payload: IAppointmentResItem[] }> = yield call(apis.quickBooking, storage.shopId.get(), payload);
    if (apt.data.payload) {
      const aptList = apt.data.payload || [];
      if (aptList?.length) {
        yield put(aptUIActions.upsertAppointments(aptList));
      }
      qbSignal.close();
      const firstItem = first(payload);
      const date = moment(firstItem?.startTime, 'MM/DD/YYYY HH:mm');
      dateParamSignal.value = date;
      calendarDatePickerRef.current?.setSelectedDate(date);

      // Booked from a caller → that caller is handled now; refresh the pending list
      // (and close the panel if it was the last one).
      if (firstItem?.callerId) {
        yield put(aptActions.getPendingCallers.fetch({ closeIfEmpty: true }));
      }

      toast.success('Booking Success');
      const staffId = firstItem?.staffId || ANYONE_ID;
      const staffsAvailable: IStaffUI[] = yield select((state: RootState) => state.shop.staffsAvailable) || [];
      const currentStaffPage = staffsAvailable.find(o => o.id?.toString() === staffId?.toString())?.page;
      if (currentStaffPage) {
        yield put(aptUIActions.setCurrentPage(currentStaffPage));
      }
      setTimeout(() => {
        const id = dateParamSignal?.value?.format('HH:mm') + '/' + (staffId);
        const element = document.getElementById(id);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }, 1000);
    }
  } catch (error) { }
  finally {
    loadingSignal.value = false;
  }
};

const setParamGetCommissionList: ISagaFunc<IApiGetQuickCommissionsParams> = function* ({ payload }) {
  const _oldParams: IApiGetQuickCommissionsParams = yield select(commSelectors.getPureParams);
  const params: IApiGetQuickCommissionsParams = {
    ..._oldParams || {},
    ...payload || {},
    pageSize: 25,
  };

  if (params.staffId === '' || params.staffId === undefined) {
    delete params.staffId;
  }

  if (params.page === 1) {
    yield put(commActions.getSummaryQuickCommissions.fetch(params));
  }

  yield put(commActions.getQuickCommissions.fetch(params));
};

const getQuickCommissions = (): ISagaFunc<IApiGetQuickCommissionsParams> => function* ({ payload }) {
  yield put(commUIActions.setLoadingTable(true));
  try {
    const res: AxiosResponse<{ payload: any }> = yield call(apis.getQuickCommissions, payload);
    if (res.data.payload) {
      yield put(commActions.getQuickCommissions.success(res.data.payload));
    }
  } catch (error) { }
  finally {
    yield put(commUIActions.setLoadingTable(false));
  }
};
const getSummaryQuickCommissions: ISagaFunc<IApiGetQuickCommissionsParams> = function* ({ payload }) {
  try {
    const res: AxiosResponse<{ payload: any }> = yield call(apis.getSummaryQuickCommissions, payload);
    if (res.data.payload) {
      yield put(commActions.getSummaryQuickCommissions.success(res.data.payload));
    }
  } catch (error) { }
};

const initQuickCommissions: ISagaFunc<IApiGetQuickCommissionsParams> = function* ({ payload }) {
  yield put(commUIActions.setLoadingTable(true));

  if (payload.page === 1) {
    yield put(commActions.getSummaryQuickCommissions.fetch(payload));
  }

  try {
    const res: AxiosResponse<{ payload: any }> = yield call(apis.getQuickCommissions, { ...payload, pageSize: 25 });
    if (res.data.payload) {
      yield put(commActions.getQuickCommissions.success(res.data.payload));
    }
  } catch (error) { }
  finally {
    yield put(commUIActions.setLoadingTable(false));
  }
};

const getOwnerReportCommissions = function* () {
  const _oldParams: IApiGetQuickCommissionsParams = yield select(commSelectors.getPureParams);
  const fromDate = moment(_oldParams.startDate, 'MM-DD-YYYY').format('YYYY-MM-DD');
  const toDate = moment(_oldParams.endDate, 'MM-DD-YYYY').format('YYYY-MM-DD');

  try {
    const res: AxiosResponse<{ payload: any }> = yield call(apis.getOwnerReportCommissions, fromDate, toDate);
    if (res.data.payload) {
      yield put(commActions.getOwnerReportCommissions.success(res.data.payload));
    }
  } catch (error) {
  }
};

function handleNativeMessage(event: MessageEvent) {
  try {
    const dataNative: { type: string, staffs: any, appointments: any, caller?: any } = JSON.parse(event?.data || '{}');
    if (dataNative?.type === 'CALENDAR_BOOTSTRAP') {
      const staffRes: IStaffResItem[] = dataNative?.staffs || [];
      if (staffRes?.length) {
        const data: IStaffUI[] = staffRes.map((o, i) => ({
          avatar: o.urlImage,
          extraData: o,
          id: o.id?.toString(),
          name: o.nickName || o.firstName,
          page: getPageNumberByIndex(i),
          pageZoom: getPageNumberByIndex(i, STAFF_CALENDAR_PAGE_ZOOM_SIZE),
          timeSchedule: o.timeSchedule,
        }));
        store.dispatch(shopActions.staffAvailable.success(data));
        store.dispatch(shopActions.staff.success(data));
      }
      const appointments: IAppointmentResItem[] = dataNative?.appointments || [];
      if (appointments?.length) {
        const data: ICalendarItemData[] = mappingAppointment(appointments);
        store.dispatch(aptActions.calendar.getData.success(data));
      }
    } else if (dataNative?.type === 'CALLER_BOOKING_REQUEST') {
      if (isCallerV2Enabled()) {
        CallerV2.open();
        return;
      }
      CallerContext.onBook(dataNative.caller);
    }
  } catch (e) {
    // ignore
  }
}

const generateRNListeners = function* () {
  yield window.addEventListener('message', handleNativeMessage);
};


const getStaffByAppointment: ISagaFunc<IApiStaffByAppointmentParams> = function* ({ payload }) {
  const pageSize: number = yield select((state: RootState) => state.apt.staffSize);
  try {
    const res: AxiosResponse<{ payload: IApiStaffByAppointmentResData }> = yield call(apis.staffByAppointment, {
      date: payload.date,
      page: 1,
      size: payload.size || pageSize,
    });
    if (res.data.payload) {
      yield put(aptActions.getStaffByAppointment.success(res.data.payload));
      if (res.data.payload.total > payload.size) {
        yield put(aptActions.getStaffByAppointment.fetchPaging({ date: payload.date, page: 1, size: res.data.payload.total }));
      }
    }
  } catch (error) {
  }
};

const getStaffByAppointmentFetchPaging: ISagaFunc<IApiStaffByAppointmentParams> = function* ({ payload }) {
  try {
    const res: AxiosResponse<{ payload: IApiStaffByAppointmentResData }> = yield call(apis.staffByAppointment, payload);
    if (res.data.payload) {
      yield put(aptActions.getStaffByAppointment.success(res.data.payload));
    }
  } catch (error) {
  }
};

const getClockInStaff = function* () {
  try {
    const res: AxiosResponse<{ payload: IStaffResItem[] }> = yield call(apis.getStaffs, storage.shopId.get());
    if (res.data.payload) {
      yield put(shopActions.clockInStaffs.success(res.data.payload.filter(o => !!o.isLockedin)));
    }
  } catch (error) {
  }
};

const getCallerIdHistory: ISagaFunc<IApiHistoryCallerIdParams> = function* ({ payload }) {
  try {
    const res: AxiosResponse<{ payload: ICallerIdHistoryResData }> = yield call(apis.historyCallerId, payload);
    if (res.data.payload) {
      yield put(aptActions.getCallerIdHistory.success(res.data.payload));
    }
  } catch (error) {
    yield put(aptActions.getCallerIdHistory.fail({}));
  }
};

// Caller V2 — today's unhandled callers. Fires on each panel open/trigger.
const getPendingCallers: ISagaFunc<{ closeIfEmpty?: boolean } | undefined> = function* ({ payload }) {
  try {
    const res: AxiosResponse<{ payload: IPendingCallerResItem[] }> = yield call(apis.getPendingCallers);
    const data = res.data.payload || [];
    yield put(aptActions.getPendingCallers.success(data));
    // Booked/SMS'd the last pending caller → nothing left to handle, close the panel.
    if (payload?.closeIfEmpty && data.length === 0) {
      CallerV2.close();
    }
  } catch (error) {
    yield put(aptActions.getPendingCallers.fail({}));
  }
};

// Caller V2 — customer brief + up to 2 recent past appointments (detail tab).
const getCustomerRecent: ISagaFunc<number> = function* ({ payload: customerId }) {
  try {
    const res: AxiosResponse<{ payload: ICustomerRecentApptsResData }> = yield call(apis.getCustomerRecentAppointments, customerId);
    if (res.data.payload) {
      yield put(aptActions.getCustomerRecent.success(res.data.payload));
    }
  } catch (error) {
    yield put(aptActions.getCustomerRecent.fail({}));
  }
};

// Caller V2 — paginated full appointment history (booking-history modal).
const getCustomerAppointments: ISagaFunc<ICustomerApptsPageParams> = function* ({ payload }) {
  try {
    const res: AxiosResponse<{ payload: ICustomerApptsPageResData }> = yield call(apis.getCustomerAppointments, payload);
    if (res.data.payload) {
      yield put(aptActions.getCustomerAppointments.success(res.data.payload));
    }
  } catch (error) {
    yield put(aptActions.getCustomerAppointments.fail({}));
  }
};

const getStaffByAppointmentSuccess: ISagaFunc<IApiStaffByAppointmentResData> = function* ({ payload }) {
  const dataApt: ICalendarItemData[] = mappingAppointment(payload.data.flatMap(o => o.appointments));
  yield put(aptActions.calendar.getData.success(dataApt));
  const dataStaff: IStaffUI[] = payload.data.map((o, i) => ({
    avatar: o.urlImage,
    extraData: o,
    id: o.id?.toString(),
    name: o.nickName || o.firstName,
    page: getPageNumberByIndex(i),
    pageZoom: getPageNumberByIndex(i, STAFF_CALENDAR_PAGE_ZOOM_SIZE),
    timeSchedule: o.timeSchedule,
  }));
  yield put(shopActions.staffAvailable.success(dataStaff));
};

const getAppointmentSummaryCallerId: ISagaFunc<IAppointmentSummaryCallerIdParams> = function* ({ payload }) {
  try {
    const resData = yield call(apis.getAppointmentSummaryCallerId, payload);
    if (resData?.data?.payload) {
      yield put(aptActions.getAppointmentSummaryCallerId.success(resData.data.payload));
    }
  } catch (error) {
    yield put(aptActions.getAppointmentSummaryCallerId.fail({}));
  } finally {
  }
};

const getCustomerTransactions: ISagaFunc<number> = function* ({ payload }) {
  try {
    const resData = yield call(apis.getCustomerTransactions, payload);
    if (resData?.data?.payload) {
      yield put(aptActions.getCustomerTransactions.success(resData.data.payload));
    }
  } catch (error) {
    yield put(aptActions.getCustomerTransactions.fail({}));
  } finally {
  }
};

const getShopSurveyInfo: ISagaFunc = function* () {
  try {
    const resData = yield call(apis.getShopSurveyInfo);
    if (resData?.data?.payload) {
      yield put(aptActions.getShopSurveyInfo.success(resData.data.payload));
    }
  } catch (error) {
    yield put(aptActions.getShopSurveyInfo.fail({}));
  } finally {
  }
};


export default function* aptSagas() {
  yield takeLatest(aptActions.calendar.getData.fetch, getCalendarData);
  yield takeLatest(aptActions.calendar.refresh, refreshCalendar);
  yield takeLatest(aptActions.initData, initData);
  yield takeLatest(aptActions.calendar.updateDrop.fetch, calendarUpdateDrop);
  yield takeLatest(aptActions.bookApt, bookApt);
  yield takeLatest(aptActions.bookAptV2, bookAptV2);
  yield takeLatest(aptActions.deleteApt, deleteApt);
  yield takeLatest(aptActions.cancelApt, cancelApt);
  yield takeLatest(aptActions.confirmApt, confirmApt);
  yield takeLatest(aptActions.checkInApt, checkInApt);
  yield takeLatest(aptActions.updateApt, updateApt);
  yield takeLatest(aptActions.updateAptV2, updateAptV2);
  yield takeLatest(aptActions.table.getData.fetch, getAppointmentLists);
  yield takeLatest(aptActions.table.getData.fetch, getAptListBlockHourList);
  yield takeLatest(aptActions.table.setParams, setTableData);
  yield takeLatest(aptActions.customer.getData.fetch, getCustomerAppointmentLists);
  yield takeLatest(aptActions.customer.setParams, setCustomerTableData);
  yield takeLatest(aptActions.customer.initData, setCustomerTableData);
  yield takeLatest(aptActions.calendar.prolongedTime, prolongedTime);
  yield takeLatest(aptActions.calendar.getData.fetch, getCalendarBlockHourList);
  yield takeLatest(aptActions.deleteBlockHour, deleteBlockHour);
  yield takeLatest(aptActions.onFormDeleteBlockHourSuccess, onFormDeleteBlockHourSuccess);
  yield takeLatest(aptActions.addBlockHour, addBlockHour);
  yield takeLatest(aptActions.updateBlockHour, updateBlockHour);
  yield takeLatest(aptActions.prolongedTimeBlockHour, prolongedTimeBlockHour);
  yield takeLatest(aptActions.quickBooking.getServices.fetch, quickBookingGetServices);
  yield takeLatest(aptActions.quickBooking.doQuickBooking, doQuickBooking);
  yield takeLatest(shopActions.staff.fetch, getStaffActions);
  yield takeLatest(shopActions.pureStaffs.fetch, getPureStaffs);
  yield takeLatest(shopActions.staffAvailable.fetch, getStaffsAvailable);
  yield takeLatest(shopActions.blockHourHistories.fetch, getBlockHourHistories);
  yield takeLatest(shopActions.scheduler.fetch, getSalonScheduler);
  yield takeLatest(shopActions.category.fetch, getCategories);
  yield takeLatest(shopActions.customer.fetch, getCustomers);
  yield takeLatest(shopActions.salonInfo.fetch, getSalonInfo);
  yield takeLatest(shopActions.shopAllSetting.fetch, getShopAllSetting);
  yield takeLatest(aptActions.staffAvailableBooking.fetch, getStaffAvailableBooking);
  yield takeLatest(commActions.setParams, setParamGetCommissionList);
  yield takeLatest(commActions.init, initQuickCommissions);
  yield takeLatest(commActions.getSummaryQuickCommissions.fetch, getSummaryQuickCommissions);
  yield takeLatest(commActions.getQuickCommissions.fetch, getQuickCommissions());
  yield takeLatest(commActions.getQuickCommissions.fetchPaging, getQuickCommissions());
  yield takeLatest(commActions.getOwnerReportCommissions.fetch, getOwnerReportCommissions);
  yield takeLatest(aptActions.getStaffByAppointment.fetch, getStaffByAppointment);
  yield takeLatest(aptActions.getStaffByAppointment.success, getStaffByAppointmentSuccess);
  yield takeLatest(aptActions.getStaffByAppointment.fetchPaging, getStaffByAppointmentFetchPaging);
  yield takeLatest(shopActions.clockInStaffs.fetch, getClockInStaff);
  yield takeLatest(aptActions.getCallerIdHistory.fetch, getCallerIdHistory);
  yield takeLatest(aptActions.getPendingCallers.fetch, getPendingCallers);
  yield takeLatest(aptActions.getCustomerRecent.fetch, getCustomerRecent);
  yield takeLatest(aptActions.getCustomerAppointments.fetch, getCustomerAppointments);
  yield takeLatest(aptActions.getAppointmentSummaryCallerId.fetch, getAppointmentSummaryCallerId);
  yield takeLatest(aptActions.blockHourList.fetch, getBlockHourList);
  yield takeLatest(aptActions.getCustomerTransactions.fetch, getCustomerTransactions);
  yield takeLatest(aptActions.getShopSurveyInfo.fetch, getShopSurveyInfo);
  yield takeLatest(shopActions.scheduleTimeOffSlots.fetch, getScheduleTimeOffSlots);
  yield generateRNListeners();
}
